// MaintenanceBanner — top-of-screen banner driven by SessionView.maintenance
// (Phase 7 Task 7-6 + Phase 9.6 dismiss extension).
//
// 表示ロジック:
//   - countdown が active (remainingSec >= 0) →
//       remainingSec >= 60: 「サーバ停止まで残り約 N 分」(分表示)
//       remainingSec < 60:  「サーバ停止まで残り N 秒」(秒表示)
//   - countdown 無し + scheduled あり → 「YYYY/MM/DD HH:mm から、約N分のメンテ」
//   - どちらも無し → 何も描画しない (`null`)
//   - × dismiss 済 (= 同 startAt の banner を一度閉じた) → null
//     新しい scheduled (startAt 違う) が来たら自動的に表示が復活する.
//     cancelled で scheduled が消えた後、再 scheduled (異 startAt) で表示.
//
// dismiss state は **memory only** (= reload で復活、localStorage 未使用、
// AnnounceBanner と同じ流儀). 重要な countdown は dismiss しても次の
// countdown push でまた強制表示するのではなく、ユーザの判断に従う (= ×
// した = 知ってる、と扱う).
//
// 旧版風の Classic UI に合わせて inline で薄い赤帯を出す. グローバル absolute
// 配置で `ClassicLayout` の上にかぶせる.

import { useEffect, useState } from 'react';

import type { MaintenanceViewState } from '../application/RoomSession.js';

export interface MaintenanceBannerProps {
  readonly state: MaintenanceViewState;
}

/**
 * 「現在 active」と判定するか. countdown は受信から 2 倍 interval (120 秒)
 * 経過したら stale 扱いで非表示にする (UI が「カウントダウンが止まったまま」
 * 表示し続けないよう). receivedAt が無いケース (= 旧データ) は常に表示.
 */
const COUNTDOWN_FRESH_MS = 120_000;

const isFreshCountdown = (
  c: { receivedAt: number; remainingSec: number } | null,
  nowMs: number,
): boolean => {
  if (c === null) return false;
  return nowMs - c.receivedAt < COUNTDOWN_FRESH_MS;
};

/**
 * countdown の残り時間を表示文言にする.
 *
 * backend の営業時間メンテ (recurring Path X) では閉店前 countdown が
 * マイルストーン式 (15/10/5/4/3/2/1 分前) で push され、`remainingSec` に
 * 900/600/300/240/180/120/60 のようなクリーン値が載る. これを「残り 900 秒」
 * ではなく「残り約 15 分」と分表示する. 単発 (admin 予約) の countdown でも
 * remainingSec が分オーダーなら同様に分表示になる.
 *
 *   - remainingSec >= 60 → 「サーバ停止まで残り約 N 分」(N = round(sec/60))
 *   - remainingSec < 60  → 「サーバ停止まで残り N 秒」(従来どおり)
 */
export const formatCountdownText = (remainingSec: number): string => {
  if (remainingSec >= 60) {
    return `サーバ停止まで残り約 ${Math.round(remainingSec / 60)} 分`;
  }
  return `サーバ停止まで残り ${remainingSec} 秒`;
};

const formatScheduledText = (scheduled: {
  startAt: number;
  durationMs?: number;
  reason?: string;
}): string => {
  const startText = new Date(scheduled.startAt).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const durationText =
    typeof scheduled.durationMs === 'number'
      ? `、約${Math.ceil(scheduled.durationMs / 60_000)}分間のメンテナンスを予定しています`
      : 'のメンテナンスを予定しています';
  const reasonText =
    typeof scheduled.reason === 'string' && scheduled.reason.length > 0
      ? `（${scheduled.reason}）`
      : '';
  return `${startText} から${durationText}${reasonText}`;
};

export const MaintenanceBanner = ({ state }: MaintenanceBannerProps): React.ReactNode => {
  const { scheduled, countdown } = state;
  // dismissed key は scheduled.startAt (= MVP は同時 active 1 件なので一意).
  // 新 scheduled (startAt 違う) で表示復活、cancelled 後の再 scheduled も同様.
  const [dismissedStartAt, setDismissedStartAt] = useState<number | null>(null);

  // 2026-05-16 fb 反映: `system.maintenance.scheduled` 再受信時 (= reducer が
  // scheduled object を作り直したとき) は dismissed を強制 clear する.
  // 旧挙動: startAt が同じだと dismissed が効いたまま banner 非表示 → ユーザは
  // 「ChatPanel に新しい告知メッセージは出てるのに banner が出ない」体感に.
  // 新挙動: scheduled object 参照が変わったタイミング (= server が再ブロードキャスト
  // した瞬間) で dismissed を null に戻す. countdown push では reducer が
  // scheduled ref を再利用するため (RoomSession.ts 466 行: `scheduled:
  // view.maintenance.scheduled`)、countdown 受信中の dismiss 保持は変わらず.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scheduled object identity の変化を tracking する目的 (= body 内で値を読まないが、reducer が新 scheduled を broadcast した瞬間に reset したい).
  useEffect(() => {
    setDismissedStartAt(null);
  }, [scheduled]);

  if (scheduled === null && countdown === null) return null;
  // dismissed 中: scheduled が同 startAt なら非表示. countdown は scheduled
  // を伴って来るので、dismissed 後の countdown も silently 抑制 (= ×した =
  // 知ってる、と扱う運用).
  if (scheduled !== null && scheduled.startAt === dismissedStartAt) return null;

  const nowMs = Date.now();
  const fresh = isFreshCountdown(countdown, nowMs);

  let text: string;
  if (fresh && countdown !== null) {
    text = formatCountdownText(countdown.remainingSec);
  } else if (scheduled !== null) {
    text = formatScheduledText(scheduled);
  } else {
    return null;
  }

  const onDismiss = (): void => {
    // scheduled の startAt を dismissed key に. scheduled が null (= countdown
    // 単独で表示中の希少ケース) では Date.now() を入れて以後の re-render で
    // dismiss 状態のままにする (新 scheduled が来たら startAt 違いで復活).
    setDismissedStartAt(scheduled?.startAt ?? Date.now());
  };

  // AnnounceBanner と同流儀: 外側 container は pointer-events-none (= banner
  // 周辺の余白で描画 canvas のクリックを通す)、内側の実 banner は
  // pointer-events-auto で × ボタンを click 可能にする.
  return (
    <div className="pointer-events-none absolute top-0 right-0 left-0 z-50">
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto flex items-center justify-center gap-2 bg-[var(--color-error)]/90 px-3 py-1 text-center text-xs font-bold text-white shadow"
      >
        <span className="flex-1">{text}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="メンテナンス告知を閉じる"
          className="cursor-pointer rounded px-1.5 text-sm leading-none opacity-80 transition hover:bg-white/20 hover:opacity-100"
        >
          ×
        </button>
      </div>
    </div>
  );
};
